import { Container, styled } from "@mui/material";
import {
  useRouteError,
  isRouteErrorResponse,
  Navigate,
  useLocation,
} from "react-router-dom";
import { isAxiosError } from "axios";
import { ErrorBoundary as SignalBoundary } from "@appsignal/react";

import { Header } from "@/features/shared/components";
import { appendStateToLoginUrl } from "@/features/shared/features/authentication";
import { appSignal } from "@/appsignal";
import { DeveloperErrorDisplay } from "@/features/shared/components/error-boundary/developer-error-display";

import Error404 from "./404";

const StyledWrapper = styled("div")({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
});

const StyledContainer = styled(Container)({
  display: "flex",
  marginTop: "9rem",
});

const isUnauthorizedError = (error: unknown) => {
  return (
    (isRouteErrorResponse(error) && error?.status === 401) ||
    (isAxiosError(error) && error.response?.status === 401)
  );
};

const ErrorBoundary = () => {
  const error = useRouteError();
  const location = useLocation();

  if (import.meta.env.MODE === "development") {
    console.error("ErrorBoundary", error);
  }

  if (isUnauthorizedError(error)) {
    return <Navigate to={appendStateToLoginUrl(location.pathname)} />;
  }

  if (import.meta.env.MODE === "development") {
    return <DeveloperErrorDisplay error={error as Error} />;
  }

  const renderError = () => {
    if (isRouteErrorResponse(error)) {
      if (error.status === 404) {
        return <Error404 />;
      }
    }
    // For now we just render the 404 page for all errors
    return <Error404 />;
  };

  return (
    <StyledWrapper>
      <SignalBoundary
        instance={appSignal}
        tags={{ component: "ErrorBoundary", location: location.pathname }}
      >
        <Header />
        <StyledContainer maxWidth="xl">{renderError()}</StyledContainer>
      </SignalBoundary>
    </StyledWrapper>
  );
};

export default ErrorBoundary;
